import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { EditIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useIdentity } from '@/features/onboarding/personal-information/hooks/use-identity';
import { PERIODIC_REVIEW_PATHS } from '@/features/periodic-review/constants';
import { useConfirmPeriodicReview } from '@/features/periodic-review/hooks/use-confirm-periodic-review';
import { useNationalClientIdentifier } from '@/features/verification/hooks/use-national-client-identifier';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileFcaQuestionnaireEligibility } from '@/hooks/queries/use-profile-fca-questionnaire';
import { useProfilePeriodicReviewAml } from '@/hooks/queries/use-profile-periodic-review';
import { isJurisdictionPermitted } from '@/utils';
import { AccountType } from '@/utils/account-type';
import { getNationalClientIdentifierTypeLabel, getPriorityCountryForCitizenships, isConcatOnlyNationalClientIdentifier } from '@/utils/national-client-identifier';

const PeriodicReviewSummary = () => {
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useIdentity();
  const { isLoading: isLoadingNationalClientIdentifier, data: nationalClientIdentifier } = useNationalClientIdentifier();
  const { isLoading: isLoadingAml, data: aml } = useProfilePeriodicReviewAml();
  const {
    isLoading: isLoadingFcaEligibility,
    data: isFcaEligible,
    isRequired: isFcaRequired,
  } = useProfileFcaQuestionnaireEligibility(identity?.addressOfResidency?.country, AccountType.SPOT);
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { confirm, isPending } = useConfirmPeriodicReview();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingIdentity || isLoadingNationalClientIdentifier || isLoadingAml || isLoadingCountries || isLoadingFcaEligibility;
  const nationalClientIdentifierAnswers = nationalClientIdentifier?.answers;

  const sections = useMemo(() => {
    const countryName = identity?.addressOfResidency?.country ? countries?.[identity.addressOfResidency.country]?.name : undefined;
    const identifierLabel = nationalClientIdentifierAnswers
      ? getNationalClientIdentifierTypeLabel(nationalClientIdentifierAnswers.identifierType, nationalClientIdentifierAnswers.country)
      : '';
    const isConcatOnly = isConcatOnlyNationalClientIdentifier(countries?.[nationalClientIdentifierAnswers?.country ?? '']?.nciPriority ?? []);

    return [
      {
        key: 'personalInformation',
        path: PERIODIC_REVIEW_PATHS.PERSONAL_INFORMATION,
        summary: [`${identity?.firstName} ${identity?.lastName}`, identity?.addressOfResidency?.locality, countryName].filter(Boolean).join(', '),
      },
      // A customer whose country only has a CONCAT isn't asked for anything, as it's built from their personal information
      ...(isConcatOnly
        ? []
        : [
            {
              key: 'nationalClientIdentifier',
              path: PERIODIC_REVIEW_PATHS.NATIONAL_CLIENT_IDENTIFIER,
              summary: nationalClientIdentifierAnswers?.identifier ? `${identifierLabel}: ${nationalClientIdentifierAnswers.identifier}` : identifierLabel,
            },
          ]),
      {
        key: 'amlQuestionnaire',
        path: PERIODIC_REVIEW_PATHS.AML_QUESTIONNAIRE,
        summary: undefined,
      },
    ];
  }, [countries, identity, nationalClientIdentifierAnswers]);

  const handlePrevStep = useCallback(() => {
    navigate(PERIODIC_REVIEW_PATHS.AML_QUESTIONNAIRE, { replace: true });
  }, [navigate]);

  if (isLoading) return <LoadingCard />;

  if (!isCompleteIdentity) return <Navigate to={PERIODIC_REVIEW_PATHS.PERSONAL_INFORMATION} replace />;

  const countryOfResidency = identity?.addressOfResidency?.country ?? '';

  if (!isJurisdictionPermitted(countryOfResidency, countries ?? {})) return <Navigate to={`${PERIODIC_REVIEW_PATHS.FORBIDDEN_JURISDICTION}/${countryOfResidency}`} replace />;

  if (isFcaRequired && !isFcaEligible) return <Navigate to={PERIODIC_REVIEW_PATHS.FCA_QUESTIONNAIRE} replace />;

  // A change of citizenship can move the customer to another country's identifiers
  const priorityCountry = getPriorityCountryForCitizenships(identity?.citizenship ?? '', identity?.citizenship2, countries ?? {});

  if (!nationalClientIdentifier || nationalClientIdentifier.answers.country !== priorityCountry) return <Navigate to={PERIODIC_REVIEW_PATHS.NATIONAL_CLIENT_IDENTIFIER} replace />;

  // Live answers load without a date, so only a draft means the customer has been through the sections
  if (!aml?.date) return <Navigate to={PERIODIC_REVIEW_PATHS.AML_QUESTIONNAIRE} replace />;

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('periodicReview.summary.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('periodicReview.summary.description')}</PageHeadingDescription>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout disabled={isPending} onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <PageCardContent className="pt-8">
          <ul className="space-y-4">
            {sections.map(({ key, path, summary }) => (
              <li className="flex gap-4 lg:gap-16 items-center rounded-md p-4 relative bg-background" key={key}>
                <div className="flex-1 min-w-0 space-y-1">
                  <h3 className="font-medium">{t(`periodicReview.summary.sections.${key}`)}</h3>
                  {summary ? <p className="text-sm text-muted-foreground break-words">{summary}</p> : null}
                </div>
                <Button
                  aria-label={`${t('fundamentals.edit')} ${t(`periodicReview.summary.sections.${key}`)}`}
                  className="-my-2 -mr-2 after:content-[''] after:absolute after:inset-0"
                  size="icon"
                  variant="link"
                  disabled={isPending}
                  onClick={() => navigate(path, { replace: true })}
                >
                  <EditIcon className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" variant="primary" disabled={isPending} onClick={confirm}>
            <span className={isPending ? 'invisible' : ''}>{t('periodicReview.summary.confirm')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </PageCard>
    </section>
  );
};
PeriodicReviewSummary.displayName = 'PeriodicReviewSummary';

export default PeriodicReviewSummary;
